import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import IconToolTip from '../../../../common/IconToolTip';
import { isPavMilestone } from '../../../../features/pav/pavConfig';

export const TicketExecutionHeader = ({ memoizedMilestones, selectedMilestones, setSelectedMilestones, headerColor, onAddBoqClick, accordionRef, defaulOpentChecklist = false, agents }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const selectedMilestone = memoizedMilestones.find(m => m.id === selectedMilestones);
    const hasManyMilestones = memoizedMilestones && memoizedMilestones.length > 1;
    const canAddBoq = !isPavMilestone(selectedMilestone);

    const getAgentTooltip = (milestoneId) => {
        if (!agents || !Array.isArray(agents)) return null;
        const sp = agents.find((a) => a.role === 'SP' && a.milestone_id === milestoneId);
        const fe = agents.find((a) => a.role === 'FE' && a.milestone_id === milestoneId);
        const spName = sp?.agent?.display_name || sp?.agent?.full_name;
        const feName = fe?.agent?.display_name || fe?.agent?.full_name;
        if (!sp && !fe) {
            return <span>SP/FE: <strong>Not Assigned</strong></span>;
        }
        if (sp && fe && sp.agent?.id === fe.agent?.id) {
            return <span>Current SP/FE: <strong>{spName}</strong></span>;
        }
        return (
            <span>
                {sp ? <>Current SP: <strong>{spName}</strong></> : <>SP: <strong>Not Assigned</strong></>}
                <br />
                {fe ? <>Current FE: <strong>{feName}</strong></> : <>FE: <strong>Not Assigned</strong></>}
            </span>
        );
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleMilestoneSelect = (milestoneId) => {
        setSelectedMilestones(milestoneId);
        setIsOpen(false);
    };


    useEffect(() => {
        if (defaulOpentChecklist && accordionRef?.current) {
            accordionRef.current.click();
        }
    }, [defaulOpentChecklist, accordionRef]);


    const isTaskAlreadyUploaded = (task) => {
        if (task.input_type === 'FILE') {
            return task.files?.length > 0;
        }
        if (task.input_type === 'MULTI_SELECT' || task.input_type === 'SELECT' || task.input_type === 'TEXT') {
            return Array.isArray(task.responses) && task.responses.length > 0;
        }
        return false;
    }

    const getTaskCompletionStats = useCallback((milestone) => {
        if (!milestone?.tasks || milestone.tasks.length === 0 || milestone?.execution_flow_required === false) {
            return { completed: 0, total: 0, allCompleted: true };
        }

        const visibleTasks = milestone.tasks.filter(task => {
            return task.visibility_conditions.every(condition => {
                const { task_id, operator, value } = condition;
                const referencedTask = milestone.tasks.find(t => t.id === task_id);
                if (!referencedTask) return true;

                const responseValue = referencedTask.responses && referencedTask.responses.length > 0
                    ? referencedTask.responses.map(response => response?.value ? response.value : response)
                    : [];

                switch (operator) {
                    case '=': return responseValue.includes(value);
                    case '!=': return !responseValue.includes(value);
                    case 'IN': return Array.isArray(value) && responseValue.some(item => value.includes(item));
                    case 'NOT_IN': return Array.isArray(value) && !responseValue.some(item => value.includes(item));
                    case 'LIKE': return responseValue.some(item => String(item).includes(value));
                    case 'NOT_LIKE': return !responseValue.some(item => String(item).includes(value));
                    case 'CONTAINS': return responseValue.includes(value);
                    case 'NOT_CONTAINS': return !responseValue.includes(value);
                    case 'EXISTS': return responseValue.length > 0;
                    case 'NOT_EXISTS': return responseValue.length === 0;
                    case 'REGEX': return responseValue.some(item => new RegExp(value).test(String(item)));
                    case 'NOT_REGEX': return !responseValue.some(item => new RegExp(value).test(String(item)));
                    case 'IS_NULL': return responseValue.length === 0;
                    case 'IS_NOT_NULL': return responseValue.length > 0;
                    default: return true;
                }
            });
        });

        const requiredTasks = visibleTasks.filter(task => task.is_required);
        const completedTasks = requiredTasks.filter(task => isTaskAlreadyUploaded(task));

        return {
            completed: completedTasks.length,
            total: requiredTasks.length,
            allCompleted: requiredTasks.length > 0 ? completedTasks.length === requiredTasks.length : true
        };
    }, []);

    return (
        <div ref={accordionRef} className='flex items-center justify-between w-full h-full'>
            {hasManyMilestones ? (
                <div className='flex gap-2 w-full'>
                    <div className='flex items-center gap-2 px-3 w-full overflow-x-auto no-scrollbar'>
                        {memoizedMilestones.map((milestone) => {
                            const stats = getTaskCompletionStats(milestone);
                            return (
                                <motion.div
                                    key={milestone.id}
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={(e) => {
                                        handleMilestoneSelect(milestone.id);
                                        if (selectedMilestones !== milestone.id) {
                                            e.stopPropagation();
                                        }
                                    }}
                                    className={`flex items-center gap-2 px-2 py-1 rounded-md cursor-pointer transition-all duration-200 shadow-sm ${selectedMilestones === milestone.id ? ' bg-opacity-90' : 'bg-opacity-70'}`}
                                    style={{ backgroundColor: selectedMilestones === milestone.id ? headerColor[milestone.id] : '#7090B040' }}
                                >
                                    {/* <span className="material-icons text-white text-xl">assignment</span> */}
                                    <IconToolTip
                                        title={
                                            <span>
                                                {getAgentTooltip(milestone.id)}
                                                <br />
                                                {selectedMilestones === milestone.id ? 'Click to Open/Close tasks' : 'Click to select'}
                                            </span>
                                        }
                                    >
                                        <h3 className="text-white font-medium text-sm whitespace-nowrap">
                                            {milestone.type}
                                        </h3>
                                    </IconToolTip>
                                    {stats && (
                                        <div className="flex items-center gap-2 bg-white bg-opacity-20 px-3 rounded-full backdrop-blur-sm">
                                            <div className="flex items-center gap-1">
                                                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                                <span className="text-white text-md font-medium">
                                                    Progress
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <span className="text-white text-md font-bold">
                                                    {stats.completed}
                                                </span>
                                                <span className="text-white text-md opacity-75">/</span>
                                                <span className="text-white text-md opacity-90">
                                                    {stats.total}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    {/* {selectedMilestones === milestone.id && (
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        className="ml-1"
                                    >
                                        <span className="material-icons text-white text-sm">check_circle</span>
                                    </motion.div>
                                    )} */}
                                </motion.div>
                            )
                        })}
                    </div>
                    {canAddBoq && <div className='w-40 ml-auto shadow-sm'>
                        <motion.button
                            type="button"
                            className="flex items-center gap-1 px-3 py-2 bg-orange-100 text-orange-700 rounded-md hover:bg-orange-200 transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                onAddBoqClick();
                            }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <span className="material-icons-outlined text-font11">inventory</span>
                            <span>Add BOQ</span>
                        </motion.button>
                    </div>}
                </div>
            ) : (
                <RenderSingleMilestone selectedMilestone={selectedMilestone} headerColor={headerColor} getTaskCompletionStats={getTaskCompletionStats} onAddBoqClick={onAddBoqClick} canAddBoq={canAddBoq} agentTooltip={getAgentTooltip(selectedMilestone?.id)} />
            )}
        </div>
    );
};



const RenderSingleMilestone = ({ selectedMilestone, headerColor, getTaskCompletionStats, onAddBoqClick, canAddBoq, agentTooltip }) => {
    const stats = getTaskCompletionStats(selectedMilestone);
    return (
        <motion.div
            whileHover={{ backgroundColor: selectedMilestone && headerColor[selectedMilestone.id] ? `${headerColor[selectedMilestone.id]}dd` : '' }}
            style={{ backgroundColor: selectedMilestone && headerColor[selectedMilestone.id] ? headerColor[selectedMilestone.id] : '#4B5563' }}
            className='flex items-center justify-between w-full h-full px-4 transition-colors duration-200'
        >
            <div className="flex items-center gap-3">
                {/* <span className="material-icons text-white text-[15px]">assignment</span> */}
                {agentTooltip ? (
                    <IconToolTip title={agentTooltip}>
                        <h3 className="text-white font-medium text-base" style={{ fontSize: '12px' }}>
                            {selectedMilestone ? `${selectedMilestone.type}` : 'Sign Off'}
                        </h3>
                    </IconToolTip>
                ) : (
                    <h3 className="text-white font-medium text-base" style={{ fontSize: '12px' }}>
                        {selectedMilestone ? `${selectedMilestone.type}` : 'Sign Off'}
                    </h3>
                )}
                {stats && (
                    <div className="flex items-center gap-2 bg-white bg-opacity-20 px-3 rounded-full backdrop-blur-sm">
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                            <span className="text-white text-md font-medium">
                                Progress
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="text-white text-md font-bold">
                                {stats.completed}
                            </span>
                            <span className="text-white text-md opacity-75">/</span>
                            <span className="text-white text-md opacity-90">
                                {stats.total}
                            </span>
                        </div>
                    </div>
                )}
            </div>
            <div className='flex gap-2'>
                {canAddBoq && <motion.button
                    type="button"
                    className="flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-md hover:bg-orange-200 transition-colors"
                    onClick={(e) => {
                        e.stopPropagation();
                        onAddBoqClick();
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    <span className="material-icons-outlined text-font11">inventory</span>
                    <span>Add BOQ</span>
                </motion.button>}
                <div className="flex items-center">
                    <span className="material-icons text-white opacity-80 text-[11px]">arrow_forward_ios</span>
                </div>
            </div>
        </motion.div>
    )
}

